import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { adminApiService } from '../../services/adminApi';
import memberService from '../../services/memberService';
import { addNotification } from '../../store/slices/uiSlice';
import * as XLSX from 'xlsx';

const AttendanceMemberFilterPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [memberQuery, setMemberQuery] = useState('');
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);

  // Event type + event/section filters
  const [eventType, setEventType] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalRecords / pageSize)), [pageSize, totalRecords]);

  useEffect(() => {
    fetchRecords();
  }, [startDate, endDate, selectedMember, currentPage, pageSize, eventType, selectedEvent, selectedSectionId]);

  useEffect(() => {
    if (!memberQuery || memberQuery.length < 2) {
      setMemberSuggestions([]);
      return undefined;
    }

    const handle = setTimeout(() => {
      fetchMemberSuggestions(memberQuery);
    }, 300);

    return () => clearTimeout(handle);
  }, [memberQuery]);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventDetails(selectedEvent.id);
    } else {
      setSections([]);
    }
  }, [selectedEvent]);

  const fetchRecords = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        start: (currentPage - 1) * pageSize,
        length: pageSize,
        order_column: '0',
        order_dir: 'desc'
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedMember?.id) params.member_id = selectedMember.id;
      if (eventType) params.event_type = eventType;
      if (selectedEvent?.id) params.event_id = selectedEvent.id;
      if (selectedSectionId) params.section_id = selectedSectionId;

      const response = await adminApiService.attendance.getRecords(params);
      setRecords(response.data || []);
      setTotalRecords(response.total || response.filtered || 0);
    } catch (err) {
      const message = err.message || t('attendance.error');
      setError(message);
      setRecords([]);
      setTotalRecords(0);
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await adminApiService.events.getAll({ skip: 0, limit: 1000 });
      const eventsData = Array.isArray(response) ? response : (response.data || []);
      setEvents(eventsData);
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: t('attendance.error') }));
    }
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const response = await adminApiService.events.getById(eventId);
      setSections(response.sections || []);
      setSelectedSectionId(null);
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: t('attendance.error') }));
    }
  };

  const fetchMemberSuggestions = async (query) => {
    try {
      setMemberLoading(true);
      const results = await memberService.searchMembers(query, 8);
      setMemberSuggestions(results);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: t('attendance.memberFilter.memberSearchError')
      }));
    } finally {
      setMemberLoading(false);
    }
  };

  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedMember(null);
    setMemberQuery('');
    setEventType('');
    setSelectedEvent(null);
    setSections([]);
    setSelectedSectionId(null);
    setCurrentPage(1);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatQRDateTime = (value) => {
    if (!value) return '-';
    if (typeof value === 'string' && value.length === 12) {
      const year = value.slice(0, 4);
      const month = value.slice(4, 6);
      const day = value.slice(6, 8);
      const hour = value.slice(8, 10);
      const minute = value.slice(10, 12);
      return `${year}-${month}-${day} ${hour}:${minute}`;
    }
    return value;
  };

  const formatMemberLabel = (member) => {
    if (!member) return '';
    const name = member.chi_name || member.eng_name || t('attendance.memberFilter.unknownMember');
    const worship = member.worship_id ? ` (${member.worship_id})` : '';
    const mobile = member.mobile ? ` · ${member.mobile}` : '';
    return `${name}${worship}${mobile}`.trim();
  };

  const renderStatus = (status) => {
    if (status === 'attended') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {t('attendance.memberFilter.attended')}
        </span>
      );
    }
    if (status === 'excused') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          {t('attendance.memberFilter.excused')}
        </span>
      );
    }
    if (status === 'not_attended') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
          {t('attendance.memberFilter.notAttended')}
        </span>
      );
    }
    return null;
  };

  const handleExportExcel = async () => {
    try {
      const params = {
        start: 0,
        length: 10000,
        order_column: '0',
        order_dir: 'desc'
      };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedMember?.id) params.member_id = selectedMember.id;
      if (eventType) params.event_type = eventType;
      if (selectedEvent?.id) params.event_id = selectedEvent.id;
      if (selectedSectionId) params.section_id = selectedSectionId;

      const response = await adminApiService.attendance.getRecords(params);
      const rows = (response.data || []).map((record) => ({
        [t('attendance.table.memberName')]: record.member_name || t('attendance.memberFilter.unknownMember'),
        [t('attendance.table.worshipId')]: (record.worship_id !== null && record.worship_id !== undefined && record.worship_id !== '') ? record.worship_id : '-',
        [t('attendance.table.mobilePhone')]: record.mobile || '-',
        [t('attendance.table.event')]: record.event_name || '-',
        [t('attendance.table.section')]: record.section_name || '-',
        [t('attendance.table.qrTime')]: formatQRDateTime(record.current_datetime),
        [t('attendance.table.checkInTime')]: formatDateTime(record.check_in_time),
        [t('attendance.table.status')]: record.status === 'attended' ? t('attendance.memberFilter.attended') : record.status === 'excused' ? t('attendance.memberFilter.excused') : t('attendance.memberFilter.notAttended')
      }));

      if (rows.length === 0) {
        dispatch(addNotification({ type: 'warning', message: t('attendance.noRecords') }));
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [18, 12, 14, 22, 16, 18, 20, 12].map(w => ({ wch: w }));

      XLSX.utils.book_append_sheet(wb, ws, t('attendance.memberFilter.title'));

      const datePart = new Date().toISOString().split('T')[0];
      const memberPart = selectedMember ? `_${selectedMember.chi_name || selectedMember.eng_name || 'member'}` : '';
      const eventPart = selectedEvent?.event_name ? `_${selectedEvent.event_name}` : '';
      const sectionPart = selectedSectionId ? `_${sections.find(s => s.id === selectedSectionId)?.section_name || selectedSectionId}` : '';
      const rangePart = (startDate || endDate) ? `_${startDate || ''}${(startDate || endDate) ? 'to' : ''}${endDate || ''}` : '';
      const filename = `AllAttendance${memberPart}${eventPart}${sectionPart}${rangePart}_${datePart}.xlsx`;

      XLSX.writeFile(wb, filename);
      dispatch(addNotification({ type: 'success', message: '已匯出出席記錄' }));
    } catch (err) {
      console.error('Export member-filter attendance failed', err);
      dispatch(addNotification({ type: 'error', message: t('attendance.error') }));
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-t-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-indigo-100">
              {t('attendance.memberFilter.subtitle')}
            </p>
            <h1 className="text-2xl font-semibold">{t('attendance.memberFilter.title')}</h1>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="px-3 py-1 bg-white/10 rounded-full">
              {t('attendance.memberFilter.filtersApplied', {
                count: [startDate, endDate, selectedMember].filter(Boolean).length
              })}
            </div>
            <button
              onClick={resetFilters}
              className="px-3 py-1 bg-white text-indigo-700 rounded-full text-sm font-medium hover:bg-indigo-50"
            >
              {t('attendance.memberFilter.clearAll')}
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('attendance.filters.from')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('attendance.filters.to')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('attendance.filters.eventType', { defaultValue: 'Event Type' })}
            </label>
            <select
              value={eventType}
              onChange={(e) => { setEventType(e.target.value); setSelectedEvent(null); setSections([]); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="">{t('common.na')}</option>
              <option value="worship">{t('attendance.tabs.worship')}</option>
              <option value="course">{t('attendance.tabs.course')}</option>
              <option value="event">{t('attendance.tabs.event')}</option>
              <option value="cell_group">{t('attendance.tabs.cellGroup')}</option>
              <option value="meeting">{t('attendance.tabs.meeting')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('attendance.filters.selectEvent')}
            </label>
            <select
              value={selectedEvent?.id || ''}
              onChange={(e) => {
                const id = e.target.value ? parseInt(e.target.value, 10) : null;
                const ev = events.find((x) => x.id === id) || null;
                setSelectedEvent(ev);
                setSections([]);
                setSelectedSectionId(null);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="">{t('common.na')}</option>
              {events
                .filter((ev) => !eventType || (ev.event_type || 'event') === eventType)
                .map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.event_name}{ev.venue ? ` (${ev.venue})` : ''}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('attendance.filters.selectSection')}
            </label>
            <select
              value={selectedSectionId || ''}
              onChange={(e) => {
                const id = e.target.value ? parseInt(e.target.value, 10) : null;
                setSelectedSectionId(id || null);
                setCurrentPage(1);
              }}
              disabled={!selectedEvent}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-gray-100"
            >
              <option value="">{t('common.na')}</option>
              {sections.map((section) => (
                <option key={section.id} value={section.id}>{section.section_name || `Section ${section.id}`}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('attendance.memberFilter.memberLabel')}
            </label>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={memberQuery}
                  onChange={(e) => { setMemberQuery(e.target.value); setSelectedMember(null); setCurrentPage(1); }}
                  placeholder={t('attendance.memberFilter.memberPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
                {memberLoading && (
                  <div className="absolute inset-y-0 right-2 flex items-center">
                    <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4"></circle>
                      <path className="opacity-75" strokeWidth="4" strokeLinecap="round" d="M4 12a8 8 0 018-8"></path>
                    </svg>
                  </div>
                )}
                {memberSuggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                    {memberSuggestions.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => {
                          setSelectedMember(member);
                          setMemberQuery(formatMemberLabel(member));
                          setMemberSuggestions([]);
                          setCurrentPage(1);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
                      >
                        <div className="font-medium text-gray-900">{member.chi_name || member.eng_name || t('attendance.memberFilter.unknownMember')}</div>
                        <div className="text-xs text-gray-500 flex space-x-2">
                          {member.worship_id && <span>{t('attendance.table.worshipId')}: {member.worship_id}</span>}
                          {member.mobile && <span>{t('attendance.table.mobilePhone')}: {member.mobile}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedMember && (
                <button
                  onClick={() => { setSelectedMember(null); setMemberQuery(''); setCurrentPage(1); }}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm"
                >
                  {t('attendance.memberFilter.clearMember')}
                </button>
              )}
            </div>
            {selectedMember && (
              <p className="text-xs text-gray-500 mt-1">{t('attendance.memberFilter.selected', { member: formatMemberLabel(selectedMember) })}</p>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-gray-700">
          {t('attendance.memberFilter.resultSummary', {
            count: totalRecords,
            dateRange: startDate || endDate ? `${startDate || '...'} → ${endDate || '...'}` : t('attendance.memberFilter.noDateRange'),
            member: selectedMember ? formatMemberLabel(selectedMember) : t('attendance.memberFilter.anyone')
          })}
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(1); }}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>{t('attendance.filters.perPage', { count: size })}</option>
            ))}
          </select>
          <button
            onClick={handleExportExcel}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-md text-sm font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v16h16V4H4zm4 4h8m-8 4h8m-8 4h8" />
            </svg>
            Export Excel
          </button>
          <div className="flex items-center space-x-1 text-sm">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('attendance.pagination.previous')}
            </button>
            <span className="px-2 text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('attendance.pagination.next')}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-3 text-gray-600">{t('attendance.loading')}</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4">
          {error}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.memberName')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.worshipId')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.mobilePhone')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.event')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.section')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.qrTime')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.checkInTime')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.status')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">{t('attendance.noRecords')}</td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {record.member_name || t('attendance.memberFilter.unknownMember')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {(record.worship_id !== null && record.worship_id !== undefined && record.worship_id !== '') ? record.worship_id : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {record.mobile || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="max-w-xs truncate">{record.event_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="max-w-xs truncate">{record.section_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {formatQRDateTime(record.current_datetime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(record.check_in_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {renderStatus(record.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AttendanceMemberFilterPage;
